import { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Chip, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchField from '../common/SearchField';
import { useGetBrandsQuery } from '../../api/master/MasterApi';
import { useGetBrandWisePageMutation } from '../../api/reports/ReportApi';
import { BrandSalesRow } from '../../types/reports';
import { exportToExcel } from '../../utils/exportExcel';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/**
 * Stock and sales for one brand, a row per product.
 *
 * <p>Three pairs of figures per product: how much was bought and what it cost, how
 * much sold and what it brought in, and how much is left and what that is worth. The
 * last pair is the one an owner acts on - it is money sitting on a shelf.
 *
 * <p>Products with nothing left show as zero rather than disappearing, because that
 * line is usually the point of the report: it is what to reorder.
 *
 * <p>Dates filter the sold columns only. Bought and remaining are the whole life of
 * the product - there is no such thing as "what is on the shelf last March" in a
 * system that counts what is physically there now.
 */
const BrandWiseReport = () => {
  const [searchValue, setSearchValue] = useState('');
  const [brandId, setBrandId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [rows, setRows] = useState<BrandSalesRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({
    bought: 0,
    boughtValue: 0,
    sold: 0,
    sales: 0,
    remaining: 0,
    remainingValue: 0,
    profit: 0,
  });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: brands = [] } = useGetBrandsQuery();
  const [getBrandWisePage] = useGetBrandWisePageMutation();

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getBrandWisePage({
          searchValue,
          brandId: brandId ? Number(brandId) : null,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
        setTotals({
          bought: Number(response?.boughtQuantity || 0),
          boughtValue: Number(response?.boughtValue || 0),
          sold: Number(response?.soldQuantity || 0),
          sales: Number(response?.salesValue || 0),
          remaining: Number(response?.remainingQuantity || 0),
          remainingValue: Number(response?.remainingValue || 0),
          profit: Number(response?.profit || 0),
        });
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, brandId, fromDate, toDate, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, brandId, fromDate, toDate]);

  const columns: GridColDef[] = [
    { field: 'brandName', headerName: 'Brand', ...centred, minWidth: 130, align: 'left', headerAlign: 'left' },
    { field: 'modelName', headerName: 'Model', ...centred, minWidth: 110 },
    { field: 'productName', headerName: 'Product', flex: 1.6, minWidth: 220, align: 'left', headerAlign: 'left' },
    {
      field: 'boughtQuantity',
      headerName: 'Bought',
      ...centred,
      minWidth: 90,
      renderCell: (row) => String(row.value ?? 0),
    },
    {
      field: 'boughtValue',
      headerName: 'Bought Value',
      ...centred,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    { field: 'soldQuantity', headerName: 'Sold', ...centred, minWidth: 90 },
    {
      field: 'salesValue',
      headerName: 'Sold Value',
      ...centred,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'remainingQuantity',
      headerName: 'Remaining',
      ...centred,
      minWidth: 100,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={Number(row.value) === 0 ? 700 : 400} color={Number(row.value) === 0 ? 'error.main' : 'inherit'}>
          {String(row.value ?? 0)}
        </Typography>
      ),
    },
    {
      field: 'remainingValue',
      headerName: 'Remaining Value',
      ...centred,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'profit',
      headerName: 'Profit',
      ...centred,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={600} color={Number(row.value) < 0 ? 'error.main' : 'success.main'}>
          {money(row.value as number)}
        </Typography>
      ),
    },
  ];

  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        Brand: row.brandName,
        Model: row.modelName,
        Product: row.productName,
        Bought: row.boughtQuantity,
        'Bought Value': row.boughtValue,
        Sold: row.soldQuantity,
        'Sold Value': row.salesValue,
        Remaining: row.remainingQuantity,
        'Remaining Value': row.remainingValue,
        Profit: row.profit,
      })),
      'brand-wise',
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: brand, model, colour, RAM, storage, barcode..." width={280} />
        <TextField select label="Brand" size="small" sx={{ minWidth: 170 }} value={brandId} onChange={(event) => setBrandId(event.target.value)}>
          <MenuItem value="">All brands</MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.brandName}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="From" size="small" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
        <TextField label="To" size="small" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(event) => setToDate(event.target.value)} />
        <Chip variant="outlined" label={`${rowCount} product(s)`} />
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.productId}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ height: '60vh' }}
      />

      {/* The totals cover the whole filter, not the page on screen. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Card sx={{ p: 2, minWidth: 320, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
            TOTALS FOR THIS FILTER
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Bought</Typography>
            <Typography variant="body2">
              {totals.bought} pcs &middot; {money(totals.boughtValue)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Sold</Typography>
            <Typography variant="body2">
              {totals.sold} pcs &middot; {money(totals.sales)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Remaining</Typography>
            <Typography variant="body2">
              {totals.remaining} pcs &middot; {money(totals.remainingValue)}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              Profit
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {money(totals.profit)}
            </Typography>
          </Stack>
        </Card>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        Dates filter the sold columns. Bought and remaining cover the whole life of the product, valued at cost.
      </Typography>
    </Box>
  );
};

export default BrandWiseReport;
